import { useEffect, useState } from "react";
import { Send, Star } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { FadeIn, Eyebrow, Tag } from "@/components/Motion";
import { apiFetch, MY5_CATEGORIES, my5Label, PawIcon, PANELS, KITTY } from "@/lib/brand";

const PANEL_BY_CATEGORY = {
    facts: "/brand/my5-facts.webp",
    "hot-takes": "/brand/scene-hottake.webp",
    faith: "/brand/my5-faith.webp",
    gaming: PANELS.gaming,
    music: PANELS.studio,
    "tv-movies": PANELS.director,
    storytimes: KITTY.laugh,
    journaling: "/brand/my5-journal.webp",
    art: PANELS.art,
    fashion: KITTY.streetwear,
    live: PANELS.stage,
};

const SAMPLES = {
    facts: ["Ideas multiply the moment you write them down.", "Most brands fail from no structure, not no talent.", "Consistency beats intensity every single month.", "Your lane gets clearer with every rep.", "Community is a cheat code — use it."],
    "hot-takes": ["\"Exposure\" never paid a bill.", "Talent without structure is a hobby.", "AI is a tool — the taste is human.", "You don't need permission to build.", "Done beats perfect. Every time."],
    faith: ["Faith over fear — daily, not just Sunday.", "Built on the Rock, not the algorithm.", "Grace is the original blueprint.", "Purpose isn't found. It's walked out.", "The cap stays on. The cross stays up."],
    music: ["Why intros matter more than hooks.", "Producer tags are sonic logos.", "The mixtape era never really died.", "Beats that built the brand.", "Album deep-dives — dropping soon."],
    "tv-movies": ["Pilots we would greenlight today.", "Soundtracks carry whole scenes.", "Writers' rooms are masterclasses.", "The reboot problem, explained.", "Finale etiquette: a survival guide."],
    storytimes: ["The day I almost quit.", "My first sale ever.", "The meeting that changed everything.", "Losing it all, learning it all.", "The DM that became a deal."],
    journaling: ["Morning pages, night moves.", "Prompt: what is the lane?", "Gratitude is structure too.", "Write the vision. Make it plain.", "One page a day keeps the drift away."],
    art: ["Sketchbook to storefront.", "Color is a language — speak it.", "Style is repetition with intent.", "Commissions 101: price the work.", "Protect your originals."],
    fashion: ["Streetwear is storytelling.", "The jersey is the uniform.", "Fit checks build brands.", "Thrift-to-runway pipeline.", "Details win: caps, chains, crosses."],
    gaming: ["Squad up: community game nights.", "Worldbuilding is brand building.", "Stream setup on a budget.", "Clutch moments, clipped right.", "GG is a mindset."],
    live: ["Going live without fear.", "The first 10 viewers matter most.", "Talk shows, PURP style.", "Live reviews, real reactions.", "Community calls — pull up."],
};

function My5Card({ label, items, badge, name, testid, img }) {
    return (
        <div className="h-full rounded-2xl bg-panel/75 backdrop-blur-md border border-white/10 p-7 hover:border-purp/50 transition-colors duration-300" data-testid={testid}>
            {img && (
                <img
                    src={img}
                    alt={`PURP ALERT mascot — ${label}`}
                    loading="lazy"
                    className="w-full aspect-square object-cover rounded-xl mb-6 border border-purp/25"
                />
            )}
            <div className="flex items-center justify-between">
                <Tag>MY 5</Tag>
                <span className="font-display font-extrabold text-purp/30 text-4xl leading-none">5</span>
            </div>
            <h3 className="mt-4 font-display font-extrabold uppercase text-2xl">{label}</h3>
            {name && <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-volt flex items-center gap-1.5"><Star className="w-3 h-3" /> by {name}</p>}
            {badge && <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">{badge}</p>}
            <ol className="mt-5 space-y-3">
                {items.map((item, j) => (
                    <li key={j} className="flex gap-3 items-baseline">
                        <span className="font-display font-bold text-purp-neon text-base shrink-0">{j + 1}</span>
                        <span className="text-slate-400 text-sm leading-relaxed">{item}</span>
                    </li>
                ))}
            </ol>
        </div>
    );
}

function SpotlightCard({ drop }) {
    const handle = (drop.handle || "").trim();
    return (
        <div className="h-full rounded-2xl bg-panel/75 backdrop-blur-md border border-purp/35 card-glow p-7" data-testid={`spotlight-card-${drop.id}`}>
            <div className="flex items-center gap-4">
                {drop.photo_url ? (
                    <img src={drop.photo_url} alt={drop.name} className="w-14 h-14 rounded-full object-cover border-2 border-purp/50 shrink-0" />
                ) : (
                    <span className="w-14 h-14 rounded-full bg-obsidian border-2 border-purp/50 flex items-center justify-center shrink-0">
                        <PawIcon className="w-6 h-6 text-purp" />
                    </span>
                )}
                <div className="min-w-0">
                    <p className="font-display font-extrabold uppercase text-xl leading-none truncate">{drop.name}</p>
                    {handle && (
                        drop.social_link ? (
                            <a href={drop.social_link} target="_blank" rel="noopener noreferrer" className="font-mono text-xs text-purp-neon hover:text-purp transition-colors" data-testid={`spotlight-handle-${drop.id}`}>
                                @{handle}
                            </a>
                        ) : (
                            <p className="font-mono text-xs text-slate-500">@{handle}</p>
                        )
                    )}
                </div>
                <span className="ml-auto shrink-0"><Tag>{my5Label(drop.category)}</Tag></span>
            </div>
            <ol className="mt-6 space-y-3">
                {drop.items.map((item, j) => (
                    <li key={j} className="flex gap-3 items-baseline">
                        <span className="font-display font-bold text-purp-neon text-base shrink-0">{j + 1}</span>
                        <span className="text-slate-400 text-sm leading-relaxed">{item}</span>
                    </li>
                ))}
            </ol>
        </div>
    );
}

export default function My5() {
    const [active, setActive] = useState("all");
    const [featured, setFeatured] = useState([]);
    const [sub, setSub] = useState({ name: "", email: "", photo_url: "", handle: "", social_link: "", category: "", items: ["", "", "", "", ""] });
    const [sending, setSending] = useState(false);

    useEffect(() => {
        apiFetch("/my5/featured").then(setFeatured).catch(() => {});
    }, []);

    const visible = active === "all" ? MY5_CATEGORIES : MY5_CATEGORIES.filter((c) => c.id === active);

    const setItem = (i) => (e) => {
        const items = [...sub.items];
        items[i] = e.target.value;
        setSub({ ...sub, items });
    };

    const submitDrop = async (e) => {
        e.preventDefault();
        if (!sub.name.trim() || !sub.category || sub.items.some((i) => !i.trim())) {
            toast.error("Name, category, and all 5 items are required.");
            return;
        }
        setSending(true);
        try {
            await apiFetch("/my5", {
                method: "POST",
                body: {
                    name: sub.name.trim(),
                    email: sub.email.trim() || undefined,
                    photo_url: sub.photo_url.trim() || undefined,
                    handle: sub.handle.trim() || undefined,
                    social_link: sub.social_link.trim() || undefined,
                    category: sub.category,
                    items: sub.items.map((i) => i.trim()),
                },
            });
            toast.success("Your MY 5 is in the inbox. If it makes the cut, it gets featured.");
            setSub({ name: "", email: "", photo_url: "", handle: "", social_link: "", category: "", items: ["", "", "", "", ""] });
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSending(false);
        }
    };

    const inputCls =
        "w-full rounded-xl bg-obsidian/80 border border-white/15 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-purp focus:ring-1 focus:ring-purp/60 transition-colors";

    return (
        <div data-testid="my5-page">
            <PageHero
                eyebrow="Signature Content Concept"
                titleLines={["It's not Top 5.", "It's MY 5."]}
                copy="Five of whatever the creator feels like sharing that day — facts, hot takes, faith, music, film, storytimes, journals, art, fashion, gaming, live. Your five. Your way. Every time."
            />

            <section className="pt-14 md:pt-20" data-testid="my5-welcome-banner">
                <div className="max-w-2xl mx-auto px-5 md:px-8">
                    <FadeIn>
                        <img
                            src="/brand/my5-welcome.webp"
                            alt="Welcome to MY FIVE — PURP ALERT #5 with all eleven MY 5 categories"
                            loading="lazy"
                            className="w-full rounded-3xl border border-purp/30 card-glow"
                            data-testid="my5-welcome-image"
                        />
                    </FadeIn>
                </div>
            </section>

            {featured.length > 0 && (
                <section className="py-16 md:py-20 border-t border-white/10" data-testid="my5-featured-section">
                    <div className="max-w-7xl mx-auto px-5 md:px-8">
                        <FadeIn>
                            <Eyebrow className="mb-5">Creator Spotlight</Eyebrow>
                            <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.95]">
                                From the <span className="italic text-purp">community.</span>
                            </h2>
                        </FadeIn>
                        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {featured.map((f, i) => (
                                <FadeIn key={f.id} delay={0.05 * (i % 6)}>
                                    <SpotlightCard drop={f} />
                                </FadeIn>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section className="py-16 md:py-24 border-t border-white/10" data-testid="my5-categories">
                <div className="max-w-7xl mx-auto px-5 md:px-8">
                    <FadeIn>
                        <div className="flex flex-wrap gap-2" data-testid="my5-filter-tabs">
                            <button
                                onClick={() => setActive("all")}
                                data-testid="my5-tab-all"
                                className={`rounded-full px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] border transition-colors duration-300 ${active === "all" ? "bg-purp text-obsidian border-purp font-bold" : "border-white/15 text-slate-400 hover:border-purp/50 hover:text-purp-neon"}`}
                            >
                                All
                            </button>
                            {MY5_CATEGORIES.map((c) => (
                                <button
                                    key={c.id}
                                    onClick={() => setActive(c.id)}
                                    data-testid={`my5-tab-${c.id}`}
                                    className={`rounded-full px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] border transition-colors duration-300 ${active === c.id ? "bg-purp text-obsidian border-purp font-bold" : "border-white/15 text-slate-400 hover:border-purp/50 hover:text-purp-neon"}`}
                                >
                                    {c.label}
                                </button>
                            ))}
                        </div>
                    </FadeIn>

                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {visible.map((c, i) => (
                            <FadeIn key={c.id} delay={0.05 * (i % 6)}>
                                <My5Card label={c.label} items={SAMPLES[c.id]} badge="Sample Drop" img={PANEL_BY_CATEGORY[c.id]} testid={`my5-card-${c.id}`} />
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            <section id="my5-submit" className="py-20 md:py-28 border-t border-white/10 relative overflow-hidden" data-testid="my5-submit-section">
                <div className="absolute inset-0 bg-radial-purp" />
                <div className="relative max-w-3xl mx-auto px-5 md:px-8">
                    <FadeIn>
                        <Eyebrow className="mb-5">Submit Your Drop</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                            Got five? <span className="italic text-purp text-glow">Say them with your chest.</span>
                        </h2>
                        <p className="mt-5 text-slate-400 text-base md:text-lg">
                            Drop your own MY 5 below. The Network reviews every submission — the best ones get
                            featured right here on this page.
                        </p>
                        <form onSubmit={submitDrop} className="mt-10 space-y-5" data-testid="my5-submit-form">
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div>
                                    <label htmlFor="my5-name" className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-2">Creator Name *</label>
                                    <input id="my5-name" className={inputCls} placeholder="Your name or handle" value={sub.name} onChange={(e) => setSub({ ...sub, name: e.target.value })} required data-testid="my5-submit-name" />
                                </div>
                                <div>
                                    <label htmlFor="my5-email" className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-2">Email (optional)</label>
                                    <input id="my5-email" type="email" className={inputCls} placeholder="So we can tell you you're featured" value={sub.email} onChange={(e) => setSub({ ...sub, email: e.target.value })} data-testid="my5-submit-email" />
                                </div>
                            </div>
                            <div className="grid sm:grid-cols-3 gap-5">
                                <div>
                                    <label htmlFor="my5-photo" className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-2">Photo URL (optional)</label>
                                    <input id="my5-photo" type="url" className={inputCls} placeholder="https://…your photo" value={sub.photo_url} onChange={(e) => setSub({ ...sub, photo_url: e.target.value })} data-testid="my5-submit-photo" />
                                </div>
                                <div>
                                    <label htmlFor="my5-handle" className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-2">Social Handle (optional)</label>
                                    <input id="my5-handle" className={inputCls} placeholder="@yourhandle" value={sub.handle} onChange={(e) => setSub({ ...sub, handle: e.target.value })} maxLength={60} data-testid="my5-submit-handle" />
                                </div>
                                <div>
                                    <label htmlFor="my5-social" className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-2">Social Link (optional)</label>
                                    <input id="my5-social" type="url" className={inputCls} placeholder="https://…your profile" value={sub.social_link} onChange={(e) => setSub({ ...sub, social_link: e.target.value })} data-testid="my5-submit-social" />
                                </div>
                            </div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600 leading-relaxed">
                                Photo, handle, and link only show if your drop is featured in the Creator Spotlight.
                            </p>
                            <div>
                                <label htmlFor="my5-category" className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-2">Category *</label>
                                <select id="my5-category" className={inputCls} value={sub.category} onChange={(e) => setSub({ ...sub, category: e.target.value })} required data-testid="my5-submit-category">
                                    <option value="" disabled>Pick your lane</option>
                                    {MY5_CATEGORIES.map((c) => (
                                        <option key={c.id} value={c.id}>{c.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-3">
                                {sub.items.map((item, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <span className="font-display font-extrabold text-purp text-xl w-5 shrink-0 text-right">{i + 1}</span>
                                        <input
                                            className={inputCls}
                                            placeholder={`Number ${i + 1}`}
                                            value={item}
                                            onChange={setItem(i)}
                                            maxLength={200}
                                            required
                                            data-testid={`my5-submit-item-${i + 1}`}
                                        />
                                    </div>
                                ))}
                            </div>
                            <button
                                type="submit"
                                disabled={sending}
                                data-testid="my5-submit-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-purp px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_44px_rgba(168,85,247,0.7)] disabled:opacity-60"
                            >
                                {sending ? "Sending..." : "Submit Your MY 5"} <Send className="w-4 h-4" />
                            </button>
                        </form>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
}
